/* eslint-disable react/prop-types */
import { Menu } from "antd";
import Sider from "antd/es/layout/Sider";
import { NavLink, useLocation } from "react-router-dom";
import { RxDashboard } from "react-icons/rx";
import { BsBook } from "react-icons/bs";
import { Form, Select, SelectItem } from "@heroui/react";
import { PiBookOpenText, PiChalkboardTeacher } from "react-icons/pi";
import { FaChalkboardTeacher } from "react-icons/fa";
import { FaRegClipboard, FaWpforms } from "react-icons/fa6";
import { IoBookOutline } from "react-icons/io5";
import { CiBellOn } from "react-icons/ci";
import { NewspaperOutlined } from "@mui/icons-material";
import { MessageCircleMore } from "lucide-react";
import { FiUsers } from "react-icons/fi";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { useGetOrganizationDataQuery } from "../../redux/apis/courseApi";
const AdminSidebar = ({ collapsed }) => {
  const { pathname } = useLocation();
  const mainPath = pathname.split("/")[2];
  const defaultRoute =
    mainPath?.slice(0).charAt(0).toUpperCase() + mainPath?.slice(1);

  const [permissions, setPermissions] = useState({});
  const [accessibleModules, setAccessibleModules] = useState([]);
  const [role, setRole] = useState('');
  
  // Get auth data
  const authData = JSON.parse(localStorage.getItem("faceAuthTokenData"));
  const access_token = authData?.token?.access;
  const apiUrl = import.meta.env.VITE_APP_API;

  useEffect(() => {
    const fetchUserPermissions = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/account/user_permissions`, {
          headers: {
            Authorization: `Bearer ${access_token}`
          }
        });
        
        const data = response.data.data || response.data; // Handle both nested and direct data
        setPermissions(data.permissions || {});
        setAccessibleModules(data.accessible_modules || []);
        setRole(data.user_role || '');
      } catch (error) {
        console.error('Error fetching user permissions:', error);
        toast.error('Failed to fetch user permissions');
      }
    };

    if (access_token) {
      fetchUserPermissions();
    }
  }, [access_token, apiUrl]);

  // Helper function to check if user has permission for a module
  const hasPermission = (permissionKey) => {
    // If user is admin, they have all permissions
    if (authData?.user?.is_organization_admin) {
      return true;
    }
    
    // Check if user has specific permission using the permissions object
    return permissions[permissionKey] === true;
  };

  const { data: organizationData } = useGetOrganizationDataQuery();
  
  // Filter sidebar items based on permissions
  const sidebarItems = [
    {
      key: "Dashboard",
      icon: <RxDashboard size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/dashboard" className="text-base font-semibold">
          Dashboard
        </NavLink>
      ),
    },
    // Manage Moderators - Only for organization admins
    authData?.user?.is_organization_admin && {
      key: "manage-moderators",
      icon: <FiUsers size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/manage-moderators" className="text-base font-semibold">
          Manage Moderators
        </NavLink>
      ),
    },
    hasPermission("can_access_courses") && {
      key: "Courses",
      icon: <BsBook size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/courses" className="text-base font-semibold">
          Courses
        </NavLink>
      ),
    },
    hasPermission("can_access_books") && {
      key: "Books",
      icon: <BsBook size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/add-book" className="text-base font-semibold">
          Books
        </NavLink>
      ),
    },
    hasPermission("can_access_form") && {
      key: "form",
      icon: <FaWpforms size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/form" className="text-base font-semibold">
          Form
        </NavLink>
      ),
    },
    hasPermission("can_access_sms_history") && {
      key: "sms",
      icon: <MessageCircleMore size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/sms" className="text-base font-semibold">
          SMS History
        </NavLink>
      ),
    },
    hasPermission("can_access_pre_booking") && {
      key: "pre-booking",
      icon: <FaWpforms size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/pre-booking" className="text-base font-semibold">
          Pre-Booking
        </NavLink>
      ),
    },
    hasPermission("can_access_mentor_activity") && {
      key: "mentor-activity",
      icon: <PiChalkboardTeacher size={20} className="flex-shrink-0" />,
      label: (
        <NavLink
          to="/admin/mentor-activity"
          className="text-base font-semibold"
        >
          Mentor Activity
        </NavLink>
      ),
    },
    hasPermission("can_access_classes") && {
      key: "Classes",
      icon: <FaChalkboardTeacher size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/classes" className="text-base font-semibold">
          Classes
        </NavLink>
      ),
    },
    hasPermission("can_access_exams") && {
      key: "Exams",
      icon: <FaRegClipboard size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/exams" className="text-base font-semibold">
          Exams
        </NavLink>
      ),
    },
    hasPermission("can_edit_profile") && {
      key: "Edit-Profile",
      icon: <FaRegClipboard size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/edit-profile" className="text-base font-semibold">
          Edit Profile
        </NavLink>
      ),
    },
    hasPermission("can_access_admission_news") && {
      key: "admission-news",
      icon: <NewspaperOutlined size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/admission-news" className="text-base font-semibold">
          Admission News
        </NavLink>
      ),
    },
    hasPermission("can_add_routine") && {
      key: "add-routine",
      icon: <IoBookOutline size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/add-routine" className="text-base font-semibold">
          Add Routine
        </NavLink>
      ),
    },
    (organizationData?.is_notification && hasPermission("can_access_notifications")) && {
      key: "Notifications",
      icon: <CiBellOn size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/notifications" className="text-base font-semibold">
          Notifications
        </NavLink>
      ),
    },
    (organizationData?.is_result && hasPermission("can_add_result")) && {
      key: "add-results",
      icon: <PiBookOpenText size={20} className="flex-shrink-0" />,
      label: (
        <NavLink to="/admin/add-results" className="text-base font-semibold">
          Add Result
        </NavLink>
      ),
    },
    organizationData?.is_banner_notification && {
      key: "banner-notifications",
      icon: <CiBellOn size={20} className="flex-shrink-0" />,
      label: (
        <NavLink
          to="/admin/banner-notifications"
          className="text-base font-semibold"
        >
          Banner Notification
        </NavLink>
      ),
    },
  ].filter(Boolean); // Remove false/null items
  return (
    <Sider
      theme="light"
      trigger={null}
      collapsible
      collapsed={collapsed}
      width="250px"
      className="h-[calc(100vh-64px)]"
    >
      <Menu
        mode="inline"
        defaultSelectedKeys={[defaultRoute]}
        items={sidebarItems}
        className="text-base font-semibold h-[calc(100%-64px)]"
      />
    </Sider>
  );
};

export default AdminSidebar;
